/**
 * Firebase Auth utilities
 */
import { getAuth } from 'firebase/auth';
import { getApps, getApp } from 'firebase/app';
import { initializeFirebase } from './config.js';
let authInstance = null;
let initializationPromise = null;
/**
 * Wait for Firebase app to be initialized with valid config
 * This ensures the frontend config initializes Firebase before we try to use it
 */
async function waitForFirebaseApp() {
    const maxWaitTime = 5000; // 5 seconds max wait
    const checkInterval = 50; // Check every 50ms
    const startTime = Date.now();
    while (Date.now() - startTime < maxWaitTime) {
        const apps = getApps();
        if (apps.length > 0) {
            const app = apps[0];
            const apiKey = app.options?.apiKey;
            // Check if API key is valid (not placeholder/missing)
            if (apiKey &&
                apiKey !== '' &&
                apiKey !== 'MISSING_API_KEY' &&
                apiKey !== 'MISSING_API_KEY_PLEASE_SET_VITE_FIREBASE_API_KEY' &&
                apiKey !== 'CONFIG_ERROR_CHECK_LOGS') {
                // Valid app found
                return;
            }
        }
        // Wait a bit before checking again
        await new Promise(resolve => setTimeout(resolve, checkInterval));
    }
    // Timeout - check if app exists but has invalid config
    const apps = getApps();
    if (apps.length > 0) {
        const app = apps[0];
        const apiKey = app.options?.apiKey;
        if (!apiKey ||
            apiKey === 'MISSING_API_KEY' ||
            apiKey === 'MISSING_API_KEY_PLEASE_SET_VITE_FIREBASE_API_KEY' ||
            apiKey === 'CONFIG_ERROR_CHECK_LOGS') {
            throw new Error('Firebase app initialized with invalid API key. ' +
                'Make sure packages/frontend/src/config/firebase.ts initializes Firebase with valid config. ' +
                'Check .env.local for VITE_FIREBASE_API_KEY.');
        }
    }
    throw new Error('Firebase app not initialized within timeout. ' +
        'Make sure packages/frontend/src/config/firebase.ts initializes Firebase before calling getFirebaseAuth().');
}
/**
 * Get Firebase Auth instance
 * Ensures Firebase is initialized with valid config before getting auth
 *
 * FIXED: Now waits for frontend config to initialize Firebase first
 */
export function getFirebaseAuth() {
    if (authInstance) {
        return authInstance;
    }
    // If we're already initializing, return the promise's result
    if (initializationPromise) {
        throw new Error('Firebase Auth initialization in progress. ' +
            'Use await getFirebaseAuthAsync() for async initialization.');
    }
    // Check if Firebase app is already initialized
    const apps = getApps();
    if (apps.length > 0) {
        const app = apps[0];
        const apiKey = app.options?.apiKey;
        // Validate API key
        if (apiKey &&
            apiKey !== '' &&
            apiKey !== 'MISSING_API_KEY' &&
            apiKey !== 'MISSING_API_KEY_PLEASE_SET_VITE_FIREBASE_API_KEY' &&
            apiKey !== 'CONFIG_ERROR_CHECK_LOGS') {
            // Valid app exists, use it
            authInstance = getAuth(app);
            if (typeof window !== 'undefined') {
                console.log('[Shared Firebase Auth] ✅ Using existing Firebase app with valid config');
            }
            return authInstance;
        }
        else {
            // App exists but has invalid config
            console.error('[Shared Firebase Auth] ❌ Firebase app exists but has invalid API key');
            console.error('[Shared Firebase Auth] API Key value:', apiKey);
            throw new Error('Firebase app initialized with invalid API key. ' +
                'Make sure packages/frontend/src/config/firebase.ts initializes Firebase with valid config. ' +
                'Check .env.local for VITE_FIREBASE_API_KEY.');
        }
    }
    // No app found - this should NOT happen if frontend config initialized properly
    console.error('[Shared Firebase Auth] ❌ No Firebase app found!');
    console.error('[Shared Firebase Auth] ❌ Frontend config should initialize Firebase first');
    console.error('[Shared Firebase Auth] ⚠️ Attempting fallback initialization (may fail)...');
    try {
        // Last resort: try to initialize (will likely fail in Electron/Vite)
        initializeFirebase();
        const app = getApp();
        authInstance = getAuth(app);
        console.warn('[Shared Firebase Auth] ⚠️ Fallback initialization succeeded (may have invalid config)');
        return authInstance;
    }
    catch (error) {
        console.error('[Shared Firebase Auth] ❌ Fallback initialization failed:', error);
        throw new Error('Firebase app not initialized. Make sure packages/frontend/src/config/firebase.ts ' +
            'initializes Firebase before the auth store tries to use it. ' +
            'Error: ' + (error instanceof Error ? error.message : String(error)));
    }
}
/**
 * Get Firebase Auth instance asynchronously
 * Waits for frontend config to initialize Firebase if needed
 */
export async function getFirebaseAuthAsync() {
    if (authInstance) {
        return authInstance;
    }
    // If already initializing, wait for that
    if (initializationPromise) {
        return initializationPromise;
    }
    // Create initialization promise
    initializationPromise = (async () => {
        try {
            // Wait for Firebase app to be initialized with valid config
            await waitForFirebaseApp();
            // Get the app
            const app = getApp();
            const apiKey = app.options?.apiKey;
            // Final validation
            if (!apiKey ||
                apiKey === 'MISSING_API_KEY' ||
                apiKey === 'MISSING_API_KEY_PLEASE_SET_VITE_FIREBASE_API_KEY' ||
                apiKey === 'CONFIG_ERROR_CHECK_LOGS') {
                throw new Error('Firebase app has invalid API key. ' +
                    'Check .env.local for VITE_FIREBASE_API_KEY.');
            }
            // Create auth instance
            authInstance = getAuth(app);
            if (typeof window !== 'undefined') {
                console.log('[Shared Firebase Auth] ✅ Auth instance created with valid config');
            }
            return authInstance;
        }
        catch (error) {
            initializationPromise = null; // Reset on error
            throw error;
        }
    })();
    return initializationPromise;
}
/**
 * Get current user
 */
export function getCurrentUser() {
    const auth = getFirebaseAuth();
    return auth.currentUser;
}
/**
 * Wait for auth state to initialize
 */
export function onAuthStateChanged(callback) {
    const auth = getFirebaseAuth();
    return auth.onAuthStateChanged(callback);
}
//# sourceMappingURL=auth.js.map